import { createContext, useEffect, useState } from "react";
import { login, logout } from "@/api/auth";
import { FlowError } from "@/errors/Flow-Error";
import { errorToast, successToast } from "@/lib/toast";
import { useStatus } from "@/hooks/use-status-handler";
import { updateAstrologer } from "@/api/astrologer";

const STORAGE_KEY = "astrologerProfile";

const pseudoAstrologer = {
  astrologerId: "",
  domains: [],
  personalInformation: {
    name: "",
    phoneNumber: "",
    picture: "",
  },
  accessToken: "",
  stats: {
    callMinutes: 0,
    chatMinutes: 0,
    experience: 0,
    rating: 0,
    orders: 0,
  },
  paymentDetails: {
    DisplayName: "",
    bankName: "",
    bankAccountNumber: "",
    ifscCode: "",
    upi: "",
  },
};

// Local Storage Helpers
//
// We intentionally strip paymentDetails before persisting so bank
// account / IFSC / UPI never live in localStorage — an XSS on the
// partner-app domain would otherwise read the astrologer's payout
// destination in one shot. Payment details remain in-memory state for
// the duration of the session; the payout settings screen must
// re-fetch from the server (GET /api/astrologer/:id/payment-details)
// when it mounts after a fresh app load.
const saveToStorage = (data) => {
  const { paymentDetails: _drop, ...safe } = data || {};
  localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
};

const fetchFromStorage = () => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : null;
};

const deleteFromStorage = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export const AuthContext = createContext({
  login: async (mobile, password) => { },
  logout: async () => { },

  updateAstrologerProfile: async (payload) => { },
  astrologer: pseudoAstrologer,
  isAuthenticated: {
    status: false,
    loaded: false,
  },
  status: {
    success: false,
    loading: false,
    error: false,
  },
});

export default function AuthProvider({ children }) {
  const [astrologer, setAstrologer] = useState(pseudoAstrologer);
  const [isAuthenticated, setIsAuthenticated] = useState({
    status: false,
    loaded: false,
  });

  const { status, errorHandler } = useStatus();

  useEffect(() => {
    const res = fetchFromStorage();
    if (!res || !res.astrologerId)
      return setIsAuthenticated({ loaded: true, status: false });

    // paymentDetails is intentionally absent from storage (XSS-safe).
    // Merge with pseudoAstrologer's empty paymentDetails shape so the
    // settings screen has a defined object to render against until it
    // re-fetches the real values from the server.
    setAstrologer({
      ...res,
      paymentDetails: pseudoAstrologer.paymentDetails,
    });
    setIsAuthenticated({ loaded: true, status: true });
  }, []);

  const loginFunc = async (mobile, password) =>
    errorHandler(async () => {
      if (!mobile || !password)
        throw new FlowError("Missing Phone Number or Password", true);

      const { data, status } = await login(mobile, password);
      if (status !== 200 || !data)
        throw new FlowError("Something went wrong! Try again later.", true);

      const {
        _id: astrologerId,
        astrologerTypes,
        callMinutes,
        chatMinutes,
        mobile: phoneNumber,
        experience,
        name,
        picture,
        rating,
        orders,
        languages,
        categories,
        gender,
        email,
        description,
        rate,
        paymentDetails,
      } = data.astrologer;

      const accessToken = data.accessToken;
      const refreshToken = data.refreshToken;

      const astrologerData = {
        astrologerId,
        domains: [astrologerTypes],
        personalInformation: {
          name,
          phoneNumber,
          picture,
          languages,
          categories,
          gender,
          email,
          astrologerTypes,
          description,
          rate,
        },
        stats: { callMinutes, chatMinutes, experience, rating, orders },
        accessToken,
        refreshToken,
        paymentDetails,
      };

      saveToStorage(astrologerData);

      setAstrologer(astrologerData);
      setIsAuthenticated({ loaded: true, status: true });
      return true;
    });

  const logoutFunc = async () => {
    const { status } = await logout(
      astrologer.astrologerId,
      astrologer.accessToken
    );
    if (status !== 200)
      return errorToast("Something went wrong! Try again later.");

    deleteFromStorage();
    setAstrologer(pseudoAstrologer);
    setIsAuthenticated({ loaded: true, status: false });
    successToast("Logged out successfully");
  };

  const updateAstrologerProfile = async (payload) => {
    try {
      const { status } = await updateAstrologer(
        astrologer.astrologerId,
        payload,
        astrologer.accessToken
      );

      if (status === 200) {
        const updatedAstrologer = {
          ...astrologer,
          personalInformation: {
            ...astrologer.personalInformation,
            name: payload.name ?? astrologer.personalInformation.name,
            email: payload.email ?? astrologer.personalInformation.email,
            languages: payload.languages ?? astrologer.personalInformation.languages,
            categories: payload.categories ?? astrologer.personalInformation.categories,
            rate: payload.rate ?? astrologer.personalInformation.rate,
            description: payload.description ?? astrologer.personalInformation.description,
            picture: payload.picture ?? astrologer.personalInformation.picture,
          },
          stats: {
            ...astrologer.stats,
            experience: payload.experience ?? astrologer.stats.experience,
          },
          paymentDetails: {
            ...astrologer.paymentDetails,
            ...(payload.paymentDetails || {}),
          },
        };

        // Update Local Storage
        saveToStorage(updatedAstrologer);

        // Update Local State
        setAstrologer(updatedAstrologer);

        return true;
      }
    } catch (error) {
      console.log(error);
      errorToast(error?.response?.data?.message || "Failed to update profile");
    }
  };



  return (
    <AuthContext.Provider
      value={{
        astrologer,
        isAuthenticated,
        login: loginFunc,
        logout: logoutFunc,
        status,
        updateAstrologerProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
